import React, { useState, useMemo, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { SalesData, ForecastData } from '../types';
import { matchesFilterCriteria, formatNumberWithCommas, isOutlierSku, isDeadSku } from '../utils/forecastUtils';
import { FilterType } from './SearchFilter';

interface SalesChartProps {
  salesData: SalesData[];
  forecasts?: ForecastData; // Make forecasts optional for historical-only view
  searchTerm: string;
  filterType: FilterType;
  startDate?: Date | null;
  endDate?: Date | null;
  title?: string;
  excludeDeadSkus?: boolean;
  excludeOutliers?: boolean;
}

// Memoize the custom tooltip component
const CustomTooltip = React.memo(({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
        <p className="label">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }}>
            {`${entry.name}: ${formatNumberWithCommas(entry.value, entry.name.includes('Revenue') ? 2 : 0)}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
});

export const SalesChart: React.FC<SalesChartProps> = React.memo(({ 
  salesData, 
  forecasts, 
  searchTerm,
  filterType = 'contains',
  startDate, 
  endDate,
  title = "Sales & Revenue Trends",
  excludeDeadSkus = false,
  excludeOutliers = true
}) => {
  const [showQuantity, setShowQuantity] = useState(true);
  const [showRevenue, setShowRevenue] = useState(true);

  const filteredData = useMemo(() => {
    return salesData.filter(item => {
      const matchesSearch = matchesFilterCriteria(item.sku, searchTerm, filterType) || 
                           matchesFilterCriteria(item.productName, searchTerm, filterType);
      if (!matchesSearch) return false;
      const itemDate = new Date(item.period + "-01");
      if (startDate && itemDate < startDate) return false;
      if (endDate && itemDate > endDate) return false;

      if (excludeDeadSkus && isDeadSku(item.sku, salesData, 60)) return false;
      if (excludeOutliers && isOutlierSku(item.sku, item.productName)) return false;

      return true;
    });
  }, [salesData, searchTerm, filterType, startDate, endDate, excludeDeadSkus, excludeOutliers]);
  
  const chartData = useMemo(() => {
    const periods = [...new Set(filteredData.map(item => item.period))].sort();
    return periods.map(period => {
      const periodData = filteredData.filter(item => item.period === period);
      const totalQuantity = periodData.reduce((sum, item) => sum + item.salesQuantity, 0);
      const totalRevenue = periodData.reduce((sum, item) => sum + item.salesRevenue, 0);
      return {
        period,
        totalQuantity,
        totalRevenue
      };
    });
  }, [filteredData]);
  
  const handleQuantityToggle = useCallback(() => {
    setShowQuantity(prev => !prev);
  }, []);

  const handleRevenueToggle = useCallback(() => {
    setShowRevenue(prev => !prev);
  }, []);
  
  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
      <div className="chart-controls">
        <label>
          <input
            type="checkbox"
            checked={showQuantity}
              onChange={handleQuantityToggle}
          />
            Show Quantity
        </label>
        <label>
          <input
            type="checkbox"
            checked={showRevenue}
              onChange={handleRevenueToggle}
          />
            Show Revenue
        </label>
        </div>
      </div>
      <div className="chart-content" style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" />
            <YAxis yAxisId="left" orientation="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip content={<CustomTooltip />} />
          <Legend />
          {showQuantity && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalQuantity"
                name="Quantity"
                stroke="#8884d8"
                dot={false}
                isAnimationActive={false}
              />
          )}
          {showRevenue && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="totalRevenue"
                name="Revenue"
                stroke="#82ca9d"
                dot={false}
                isAnimationActive={false}
              />
          )}
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}); 